import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HopeReport } from "models/hope/hope-report";
import * as moment from "moment";
import * as momentTZ from "moment-timezone";
import { HopeReportType } from "pages/reports/components/report-hope-popover/report-hope-popover";
import { StateManagementService } from "providers/state-management/state-management.service";
import { Subject } from "rxjs";
import { tap } from "rxjs/operators";
import { AuthService } from "../../app/auth/auth.service";
import { HopeVisitSignature } from "../../models/hope/hope-type";
import { commonVar } from "../common-var/common";
import { ConfigurationService } from "../configuration/configuration.service";
import { ResourceManagementService } from "../resource-management/resource-management.service";
import { UtilityService } from "../utility-service/utility-service";

interface Tab {
  name: string;
  icon: string;
  title: string;
  show: boolean;
}

@Injectable()
export class HopeProvider {
  private baseUrl: string;
  private hqrpUrl: string;
  public patientId: string;
  public tenantId: string;
  public hopeReportDetail;
  public activeReportId: string;
  public lookupValues: any;
  public validSignature: string = "";
  public hopeReportList: HopeReport[] = [];
  public hopeReportDetailChange = new Subject();
  public isBillingFacility: boolean = false;
  public z0400signatures: HopeVisitSignature[];

  public tabList = [];

  getTabList(hopeType: HopeReportType): Tab[] {
    const isDischarge = hopeType === "DISCHARGE";
    const isHuv = hopeType === "HUV1" || hopeType === "HUV2";
    return [
      {
        name: "hopeAdminInfo",
        icon: commonVar.ADD_ICON,
        title: "A - Administrative Information",
        show: true,
      },
      ...(!isDischarge && !isHuv
        ? [
            {
              name: "preference",
              icon: commonVar.ADD_ICON,
              title: "F - Preferences",
              show: true,
            },
            {
              name: "hopeActiveDiagnosis",
              icon: commonVar.ADD_ICON,
              title: "I - Active Diagnosis",
              show: true,
            },
          ]
        : []),
      ...(!isDischarge
        ? [
            {
              name: "hopeHealthCondition",
              icon: commonVar.ADD_ICON,
              title: "J - Health Conditions",
              show: true,
            },
            {
              name: "hopeSkinCondition",
              icon: commonVar.ADD_ICON,
              title: "M - Skin Conditions",
              show: true,
            },
            {
              name: "medication",
              icon: commonVar.ADD_ICON,
              title: "N - Medications",
              show: true,
            },
          ]
        : []),
      {
        name: "hopeRecordAdministration",
        icon: commonVar.ADD_ICON,
        title: "Z - Record Administration",
        show: true,
      },
    ];
  }

  public patientStatus: string;
  orgTimezone: string;

  constructor(
    public http: HttpClient,
    private config: ConfigurationService,
    private utilService: UtilityService,
    private authSvc: AuthService,
    private resourceManagementService: ResourceManagementService,
    private stateManagementService: StateManagementService
  ) {
    let hisAdmissionUrl = config.getConfig().hisAdmissionUrl;
    if (
      this.config.getConfig().isLocal &&
      this.config.getConfig().localEndpoints.hisAdmissionUrl
    ) {
      hisAdmissionUrl = this.config.getConfig().localEndpoints.hisAdmissionUrl;
    }

    this.baseUrl = hisAdmissionUrl + "/his/";
    this.hqrpUrl = hisAdmissionUrl + "/hqrp/";
    this.getOrgTimezone();
    this.getNameSignatureByExternalId();
    this.tabList = this.getTabList(null);
  }
  getNameSignatureByExternalId() {
    this.resourceManagementService
      .getNameForSignatureByExternalId(this.authSvc.getUser().qbosUserId)
      .subscribe((name) => {
        this.validSignature = name.toString();
      });

    this.stateManagementService.newBillingFacility.subscribe((x) => {
      if (x !== null) {
        this.isBillingFacility = x;
      }
    });
  }

  getSignatureByExternalId() {
    if (!this.hopeReportDetail?.createdBy) {
      console.warn("No createdBy ID available for signature retrieval");
      return;
    }

    this.resourceManagementService
      .getProviderWithCredentialDescriptions(this.hopeReportDetail.createdBy)
      .subscribe({
        next: (providerData) => {
          const hopeProviderSignatureDetails: HopeVisitSignature = {
            firstName: providerData.firstName,
            middleName: providerData.middleName,
            lastName: providerData.lastName,
            credentials: providerData.credentialDescriptions,
            sections: ["All"],
            sectionCompletedDate: this.hopeReportDetail.createdAt,
          };
          this.z0400signatures = [hopeProviderSignatureDetails];
        },
        error: (error) => {
          console.error("Error retrieving signature data:", error);
          // Set default signature structure on error
          this.z0400signatures = [
            {
              firstName: "",
              middleName: "",
              lastName: "",
              credentials: [],
              sections: ["All"],
              sectionCompletedDate: this.hopeReportDetail.createdAt,
            },
          ];
        },
      });
  }

  // NOVA-1707: DISCHARGE Z0400 signer = the clinician who performed the postmortem encounter.
  // his-service stores that performer's id on record_administration.userId (from the postmortem
  // VisitCompletedEvent) and the completion date in Z0400B. Postmortem visits are NOT e-signed, so
  // there is no stored signer name — resolve it from userId exactly like the admission signer. This
  // replaces the old, incorrect source (the patient-chart-status DISCHARGED row's audit username +
  // created date, which reflect whoever wrote the status row on any replay/backfill).
  getDischargeSignature() {
    const recordAdministration =
      this.hopeReportDetail && this.hopeReportDetail.hopeRecordAdministration;
    const userId = recordAdministration && recordAdministration["userId"];
    if (!userId) {
      this.z0400signatures = [];
      return;
    }
    const sectionCompletedDate = this.z0400DateToSectionCompletedDate(
      recordAdministration["Z0400B"]
    );
    this.resourceManagementService
      .getProviderWithCredentialDescriptions(userId)
      .subscribe({
        next: (providerData) => {
          this.z0400signatures = [
            {
              firstName: providerData.firstName,
              middleName: providerData.middleName,
              lastName: providerData.lastName,
              credentials: providerData.credentialDescriptions,
              sections: ["A"],
              sectionCompletedDate,
            },
          ];
        },
        error: (error) => {
          console.error("NOVA-1707: discharge signer lookup failed:", error);
          this.z0400signatures = [
            {
              firstName: "",
              middleName: "",
              lastName: "",
              credentials: [],
              sections: ["A"],
              sectionCompletedDate,
            },
          ];
        },
      });
  }

  // NOVA-1707: convert stored Z0400B (his-service format "yyyyMMdd'T'HHmmssZ") into the
  // sectionCompletedDate epoch the Z0400 table renders via moment.utc(date).format(...). Build an
  // epoch whose UTC calendar fields equal the stored wall-clock date, so it shows the same calendar
  // day regardless of the viewer's browser timezone.
  private z0400DateToSectionCompletedDate(z0400b: string): number {
    if (!z0400b) {
      return null;
    }
    const strict = moment.parseZone(z0400b, "YYYYMMDD[T]HHmmssZZ", true);
    const parsed = strict.isValid() ? strict : moment.parseZone(z0400b);
    if (!parsed.isValid()) {
      return null;
    }
    return Date.UTC(
      parsed.year(),
      parsed.month(),
      parsed.date(),
      parsed.hour(),
      parsed.minute(),
      parsed.second()
    );
  }

  setTabIcons(hopeType: HopeReportType) {
    this.tabList = this.getTabList(hopeType);
    this.tabList.forEach((tab) => {
      const sectionData = this.hopeReportDetail[tab.name];

      if (this.isSectionInProgress(sectionData))
        tab.icon = commonVar.IN_PROGRESS_ICON;

      if (sectionData.complete) tab.icon = commonVar.COMPLETED_ICON;
    });
  }

  isSectionInProgress(sectionData: any): boolean {
    let result = false;

    if (!sectionData) return result;

    const objKeys = Object.keys(sectionData).filter(
      (key) => key !== "complete" && key !== "isComplete"
    );

    objKeys.forEach((key) => {
      if (sectionData[key] instanceof Array && sectionData[key].length == 0) {
        return false;
      } else {
        if (sectionData[key] && sectionData[key] !== "") {
          result = true;
        }
      }
    });

    return result;
  }

  // List of all HQRP (HIS/HOPE) Reports for a given patient id
  getHqrpReportList(page: number = 0) {
    const resourceUrl = `${this.hqrpUrl}report/patient/${this.patientId}/search?page=${page}&size=100`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http.get(resourceUrl, { headers });
  }

  setHopeReportDetail(reportId) {
    const resourceUrl = `${this.hqrpUrl}hope/report/${reportId}`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    return this.http
      .get(resourceUrl, { headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((reportDetail) => {
          this.hopeReportDetail = reportDetail;
          this.setTabIcons(this.hopeReportDetail.hopeType);
        })
      );
  }

  deleteHopeReport(reportId) {
    const resourceUrl = `${this.hqrpUrl}hope/report/${reportId}`;
    return this.http.delete(resourceUrl);
  }

  prePopulateNewReport(hopeType?: string) {
    return this.http
      .get(`${this.hqrpUrl}hope/report/patient/${this.patientId}/init`, {
        params: hopeType
          ? {
              hopeType: hopeType,
            }
          : undefined,
      })
      .pipe(
        tap((reportDetail) => {
          this.hopeReportDetail = reportDetail;
          this.hopeReportDetail.initialValue = true;
          this.setTabIcons(this.hopeReportDetail.hopeType);
        })
      );
  }

  mapZipNumber(reportDetail) {
    const zipNumber = reportDetail.hopeAdminInfo.A0550;
    if (zipNumber && zipNumber.length > 5) {
      reportDetail.hopeAdminInfo.A0550A = zipNumber.substring(0, 5);
      reportDetail.hopeAdminInfo.A0550B = zipNumber.substring(5);
    } else {
      reportDetail.hopeAdminInfo.A0550A = zipNumber;
      reportDetail.hopeAdminInfo.A0550B = "";
    }

    return reportDetail;
  }

  updateHopeReport(formSection, data) {
    const url = `${this.hqrpUrl}hope/report/${this.activeReportId}`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");

    const update = {
      reportId: this.activeReportId,
      patientId: this.patientId,
      type: "hope",
      timezone: this.orgTimezone,
      endDate: this.getDateWithOrg(),
    };

    update[formSection] = data;

    return this.http
      .put(url, update, { headers: headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((reportDetail) => {
          this.hopeReportDetail = reportDetail;
          this.hopeReportDetailChange.next(reportDetail);
          this.hopeReportDetail = reportDetail;
          this.setTabIcons(this.hopeReportDetail.hopeType);
        })
      );
  }

  createHopeReport(newReport) {
    const resourceUrl = `${this.hqrpUrl}hope/report/patient/${this.patientId}`;
    const headers = new HttpHeaders().set("Content-Type", "application/json");
    const payload = {
      ...newReport,
      type: "hope",
      patientId: this.patientId,
      timezone: this.orgTimezone,
      startDate: this.getDateWithOrg(),
    };

    return this.http
      .post(resourceUrl, payload, { headers })
      .map((reportDetail) => this.initializeNullValues(reportDetail))
      .pipe(
        tap((newReport: any) => {
          this.hopeReportDetail = newReport;
          this.activeReportId = newReport.id;
          this.hopeReportDetailChange.next(newReport);
          this.setTabIcons(this.hopeReportDetail.hopeType);
        })
      );
  }

  isValidSignature(): boolean {
    if (
      this.hopeReportDetail.hopeAdminInfo.A0050 === "1" &&
      this.getARListCount() > 1 &&
      this.checkSectionsCompletion() &&
      !this.hopeReportDetail.endDate
    ) {
      return (
        this.hopeReportDetail.hopeRecordAdministration.Z0500A ===
        this.validSignature
      );
    } else {
      return true;
    }
  }

  getARListCount(): number {
    for (let i = 0; i < this.hopeReportList.length; i++) {
      if (this.hopeReportList[i].reportStatus === "EXPORTED") {
        return 2;
      }
    }
    return 0;
  }

  initializeNullValues(reportDetail) {
    var newReportDetail = reportDetail;

    if (!newReportDetail.hopeAdminInfo) newReportDetail.hopeAdminInfo = {};
    if (!newReportDetail.hopeHealthCondition)
      newReportDetail.hopeHealthCondition = {};
    if (!newReportDetail.medication) newReportDetail.medication = {};
    if (!newReportDetail.preference)
      newReportDetail.preference = { F2000A: "" };
    if (!newReportDetail.hopeRecordAdministration)
      newReportDetail.hopeRecordAdministration = {};

    return newReportDetail;
  }

  formatDate(date) {
    return date ? moment(date).format("MM-DD-YYYY") : null;
  }

  formatDateYYYYMMDD(date) {
    return date ? moment(date).format("YYYYMMDD") : null;
  }

  formatUTCDatetoLocal(date) {
    if (date) {
      date = moment.utc(date).local().format();
    }

    return date ? date : null;
  }

  formatlocalDatetoUTC(date) {
    if (date) {
      date = moment.parseZone(date).utc().format("YYYYMMDDTHHmmssZZ");
    }
    return date ? date : null;
  }

  // Returns true if all sections complete
  checkSectionsCompletion(): boolean {
    return this.tabList
      .filter((tab) => tab.name !== "hopeRecordAdministration")
      .every((tab) => this.hopeReportDetail[tab.name]?.complete === true);
  }

  setTenantId(tenantId) {
    this.tenantId = tenantId;
  }

  getTenantId() {
    return this.tenantId;
  }

  getPatientId() {
    return this.patientId;
  }

  getPatientInformation() {
    if (this.patientId) {
      const resourceUrl = `${this.baseUrl}patient/${this.patientId}?billingFacilityToggle=${this.isBillingFacility}`;
      const headers = new HttpHeaders().set("Content-Type", "application/json");
      return this.http.get(resourceUrl, { headers });
    }
  }

  getDateWithOrg(): string {
    let date = this.utilService.convertDateTimeWithTimezone(
      new Date(),
      this.orgTimezone
    );
    return date;
  }

  getOrgTimezone() {
    this.orgTimezone = this.authSvc.getUser().timeZoneOrg;
    // In case a valid time zone is not returned from RBAC, let's use the user/browser time zone
    if (!this.orgTimezone) {
      this.orgTimezone = momentTZ.tz.guess();
    }
  }

  createModificationOrInactivationFromExistingReport(
    reportId: number,
    recordType: "2" | "3" // 2- Modification, 3 - Inactivation
  ): any {
    return this.http
      .get(`${this.hqrpUrl}hope/report/${reportId}/recordType/${recordType}`)
      .pipe(
        tap((reportDetail) => {
          this.hopeReportDetail = reportDetail;
          this.hopeReportDetail.initialValue = true;
          this.setTabIcons(this.hopeReportDetail.hopeType);
        })
      );
  }

  evaluateSectionCompletion(tabName: string) {
    const section = this.hopeReportDetail?.[tabName];
    if (section) {
      section.complete = this.checkSectionCompleted(section);
      this.setTabIcons(this.hopeReportDetail?.hopeType);

      this.tabList = [...this.tabList];
    }
  }

  checkSectionCompleted(section: any): boolean {
    if (!section) return false;
    const keys = Object.keys(section).filter(
      (key) => key !== "complete" && key !== "isComplete"
    );

    return keys.every((key) => {
      const value = section[key];
      return Array.isArray(value)
        ? value.length > 0
        : value !== null && value !== "";
    });
  }
}



// WEBPACK FOOTER //
// ./src/providers/his-service/hope-provider.ts